import { normalizePostPreviewMedia } from './postPreviewMedia'

function firstString(...values) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

function firstValue(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value
  }
  return null
}

function firstNonEmptyArray(...values) {
  return values.find((value) => Array.isArray(value) && value.length > 0) || []
}

function objectOrNull(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : null
}

function canonicalPreviewPayload(source) {
  const payload = source?.payload || source || {}
  const feedPayload = objectOrNull(payload.feed_payload)
  if (!feedPayload) return payload

  return {
    ...payload,
    ...feedPayload,
    id: payload.id || feedPayload.id,
    post_id: payload.post_id || payload.id || feedPayload.post_id || feedPayload.id,
  }
}

function normalizeCta(cta) {
  if (!cta) return null
  return {
    ...cta,
    kind: cta.kind === 'provider-cart'
      ? 'provider_cart'
      : cta.kind === 'deep-link'
        ? 'deep_link'
        : cta.kind,
  }
}

function normalizeCreator(payload) {
  const creator = objectOrNull(payload.creator) || objectOrNull(payload.user)
  if (creator) return creator

  const id = firstString(payload.creator_id, payload.user_id)
  const displayName = firstString(payload.creator_name, payload.display_name)
  const avatarUrl = firstString(payload.creator_avatar, payload.avatar_url)
  if (!id && !displayName && !avatarUrl) return {}

  return {
    id,
    display_name: displayName,
    avatar_url: avatarUrl,
  }
}

function previewCta(payload) {
  const cta = objectOrNull(payload.cta)
  if (cta) return normalizeCta(cta)

  const kind = firstString(payload.cta_kind)
  const label = firstString(payload.cta_label)
  const target = firstString(payload.cta_target)
  if (!kind && !label && !target) return null

  return normalizeCta({ kind, label, target })
}

function normalizeMedia(items = []) {
  return items
    .map((item, index) => {
      const normalized = normalizePostPreviewMedia(item)
      if (!normalized) return null
      return {
        id: normalized.id,
        type: normalized.type,
        url: normalized.url,
        sort_order: normalized.sort_order ?? item.sort_order ?? index,
        playback: normalized.type === 'video' ? {
          hls_url: normalized.hls_manifest_url,
          fallback_url: normalized.fallback_url,
          poster_url: normalized.poster_url,
          ...(normalized.captions?.length ? { captions: normalized.captions } : {}),
        } : undefined,
      }
    })
    .filter(Boolean)
}

function normalizeTags(tags = []) {
  return tags.map((tag) => ({
    label: tag.label || '',
    position: tag.position || null,
    style: tag.style || {
      background_color: tag.bg_color || tag.background_color,
      text_color: tag.text_color,
      icon: tag.icon || null,
      font_style: tag.font_style || null,
    },
  }))
}

function normalizeOdds(leg, selection = {}) {
  const odds = leg.odds || selection.odds || {}
  if (odds && typeof odds === 'object' && Object.keys(odds).length > 0) return odds

  const american = firstValue(leg.odds_american, leg.american_odds, selection.odds_american)
  if (american) return { american }

  const decimal = firstValue(leg.odds_decimal, leg.decimal_odds, selection.odds_decimal)
  if (decimal) return { decimal }

  if (typeof odds === 'string' || typeof odds === 'number') return odds
  return {}
}

function normalizePreviewSelection(leg, index, postId) {
  const rawSelection = objectOrNull(leg.selection) || {}
  const rawMarket = objectOrNull(leg.market) || objectOrNull(rawSelection.market) || {}
  const rawEvent = objectOrNull(leg.event) || objectOrNull(rawSelection.event) || {}
  const selectionMetadata = objectOrNull(leg.selection_metadata) || objectOrNull(rawSelection.metadata) || {}
  const marketMetadata = objectOrNull(leg.market_metadata) || objectOrNull(rawMarket.metadata) || {}
  const eventMetadata = objectOrNull(leg.event_metadata) || objectOrNull(rawEvent.metadata) || {}
  const providerPayload = objectOrNull(leg.provider?.payload)
    || objectOrNull(leg.provider_ref?.payload)
    || objectOrNull(leg.provider_payload)
    || objectOrNull(leg.cart_item)
    || objectOrNull(rawSelection.provider_payload)
    || objectOrNull(rawSelection.provider_ref?.payload)
    || selectionMetadata
    || {}
  const providerSelectionId = firstValue(
    leg.provider?.selection_id,
    leg.provider_ref?.selection_id,
    rawSelection.provider_selection_id,
    rawSelection.outcome_id,
    leg.outcome_id,
    leg.external_id,
    leg.selection_id,
  )
  const providerKey = firstString(
    leg.provider?.key,
    leg.provider_ref?.key,
    providerPayload.provider,
    providerSelectionId ? 'kambi' : '',
  )
  const selectionName = firstString(
    leg.label,
    leg.selection_name,
    rawSelection.label,
    rawSelection.name,
    rawSelection.outcome_label,
  )
  const marketName = firstString(
    leg.subtitle,
    marketMetadata.criterion_label,
    leg.market_name,
    rawMarket.name,
    rawMarket.type,
    rawSelection.market_name,
  )
  const eventName = firstString(
    leg.event_name,
    rawEvent.name,
    rawSelection.event_name,
  )
  const selectionId = String(firstValue(rawSelection.id, leg.selection_id, providerSelectionId, `${postId}-selection-${index}`))

  return {
    id: selectionId,
    sort_order: leg.sort_order ?? index,
    label: selectionName,
    subtitle: marketName,
    status: leg.status || leg.result || rawSelection.status || '',
    odds: normalizeOdds(leg, rawSelection),
    selection: {
      id: selectionId,
      name: selectionName,
      type: rawSelection.type || leg.selection_type || selectionMetadata.outcome_type || '',
      outcome_id: rawSelection.outcome_id || leg.outcome_id || null,
    },
    market: {
      id: rawMarket.id || leg.market_id || '',
      name: marketName,
      type: rawMarket.type || leg.market_type || '',
      points: rawMarket.points ?? leg.market_points ?? leg.points ?? null,
      metadata: marketMetadata,
    },
    event: {
      id: rawEvent.id || leg.event_id || '',
      name: eventName,
      start_time: rawEvent.start_time || leg.event_start || leg.start_time || null,
      status: rawEvent.status || leg.event_status || '',
      metadata: eventMetadata,
      participants: {
        home: rawEvent.participants?.home || rawEvent.home || leg.home_participant || eventMetadata.home_participant || null,
        away: rawEvent.participants?.away || rawEvent.away || leg.away_participant || eventMetadata.away_participant || null,
      },
    },
    provider_ref: {
      key: providerKey,
      selection_id: providerSelectionId ? String(providerSelectionId) : '',
      event_id: leg.provider?.event_id || rawEvent.external_id || leg.event_id || null,
      market_id: firstValue(
        leg.provider?.market_id,
        leg.provider_ref?.market_id,
        rawMarket.external_id,
        providerPayload.bet_offer_id,
      ),
      payload: providerPayload,
    },
    provider: {
      key: providerKey,
      selection_id: providerSelectionId ? String(providerSelectionId) : '',
      event_id: leg.provider?.event_id || rawEvent.external_id || leg.event_id || null,
      payload: providerPayload,
    },
  }
}

function normalizeSelectionSet(payload, selections) {
  if (payload.selection_set && typeof payload.selection_set === 'object') {
    return {
      ...payload.selection_set,
      selections: Array.isArray(payload.selection_set.selections)
        ? payload.selection_set.selections.map((selection, index) => normalizePreviewSelection(selection, index, payload.id || payload.post_id || 'preview'))
        : selections,
    }
  }

  return {
    schema_version: '1.0',
    source: {},
    summary: {
      kind: selections.length === 1 ? 'single' : selections.length > 1 ? 'provider_defined' : 'none',
      label: selections.length === 1 ? 'single' : selections.length > 1 ? 'parlay' : 'No selections',
      selection_count: selections.length,
      odds: selections.length === 1 ? selections[0]?.odds : undefined,
    },
    selections,
    groups: [],
    entries: [],
  }
}

export function normalizeFeedPreviewItem(source, fallbackId = 'preview') {
  const payload = canonicalPreviewPayload(source)
  const postId = String(source?.id || payload.id || payload.post_id || fallbackId)
  const media = normalizeMedia(payload.media || payload.media_objects || [])
  const canonicalSelections = objectOrNull(payload.selection_set)?.selections
  const selectionSource = firstNonEmptyArray(
    canonicalSelections,
    payload.legs,
    payload.bet_legs,
  )
  const selections = selectionSource
    .map((leg, index) => normalizePreviewSelection(leg, index, postId))
  const tags = normalizeTags(payload.tags || [])
  const cardPresentation = objectOrNull(payload.card_presentation)
    ? { ...payload.card_presentation }
    : null
  if (cardPresentation) delete cardPresentation.accent

  return {
    id: postId,
    kind: payload.kind || (payload.post_type === 'promotion' ? 'promotion' : 'post'),
    text: payload.text || '',
    title: payload.title || '',
    subtitle: payload.subtitle || '',
    created_at: payload.created_at || source?.created_at,
    expires_at: payload.expires_at || null,
    creator: normalizeCreator(payload),
    content_origin: payload.content_origin || source?.content_origin,
    media,
    legs: selections,
    selection_set: normalizeSelectionSet(payload, selections),
    cta: previewCta(payload),
    card_presentation: cardPresentation,
    tags,
    share_enabled: payload.share_enabled !== false,
    copy_count: typeof payload.copy_count === 'number' ? payload.copy_count : undefined,
    is_shared_entry: Boolean(payload.is_shared_entry),
  }
}

export function previewItemHasContent(source) {
  const item = normalizeFeedPreviewItem(source)
  return Boolean(
    String(item.text || '').trim()
    || String(item.title || '').trim()
    || String(item.subtitle || '').trim()
    || item.media.length
    || item.legs.length
    || (item.cta && (item.cta.kind || item.cta.label || item.cta.target))
  )
}

export function getPreviewSelections(item) {
  if (!item) return []
  const normalized = normalizeFeedPreviewItem(item)
  return Array.isArray(normalized.selection_set?.selections)
    ? normalized.selection_set.selections
    : normalized.legs || []
}

export function suggestionToFeedPreviewItem(
  suggestion,
  { creator = null, fallbackId = 'suggestion-preview' } = {},
) {
  const evaluation = suggestion?.evaluation || {}
  const parlay = suggestion?.parlay || {}
  const spec = evaluation.canonical_spec || {
    legs: parlay.legs,
    provider: parlay.provider,
    offering_id: parlay.offering_id,
  }
  const quote = evaluation.provider_plan?.quote || {
    decimal_odds: parlay.price_decimal,
    price_kind: parlay.price_kind,
  }
  const suggestionId = String(suggestion?.id || fallbackId)
  const legs = Array.isArray(spec.legs)
    ? spec.legs.map((leg, index) => ({
      selection_id: String(leg.outcome_id || `${suggestionId}-selection-${index}`),
      outcome_id: leg.outcome_id || null,
      event_id: leg.event_id || '',
      event_start: leg.event_start || null,
      label: leg.outcome_label || '',
      subtitle: leg.market_label || '',
      event_name: leg.event_label || '',
      line: leg.line ?? null,
      decimal_odds: leg.current_decimal_odds || null,
      status: leg.status || '',
      market_family: leg.market_family || '',
      provider: {
        key: spec.provider || evaluation.provider || 'kambi',
        selection_id: leg.outcome_id || '',
        event_id: leg.event_id || null,
        market_id: leg.bet_offer_id || null,
      },
      provider_payload: {
        outcome_id: leg.outcome_id || null,
        event_id: leg.event_id || null,
        bet_offer_id: leg.bet_offer_id || null,
        line: leg.line ?? null,
      },
    }))
    : []
  const totalOdds = quote.decimal_odds ? { decimal: quote.decimal_odds } : undefined
  const selectionIds = legs.map((leg) => leg.selection_id)

  return normalizeFeedPreviewItem({
    id: suggestionId,
    payload: {
      id: suggestionId,
      title: suggestion?.title || suggestion?.text || '',
      text: suggestion?.text || suggestion?.title || '',
      content_origin: 'generated',
      creator: creator || suggestion?.creator || null,
      expires_at: suggestion?.expires_at || parlay.valid_until || null,
      legs,
      selection_set: {
        schema_version: '2.0',
        source: {
          provider_key: spec.provider || evaluation.provider || 'kambi',
          offering_id: spec.offering_id || suggestion?.offering_id || null,
          generation_run_id: suggestion?.generation_run_id || parlay.generation_run_id || null,
        },
        summary: {
          kind: 'provider_defined',
          label: 'parlay',
          selection_count: legs.length,
          ...(totalOdds ? { odds: totalOdds } : {}),
          price_kind: quote.price_kind || undefined,
        },
        selections: legs,
        groups: Array.isArray(spec.groups) ? spec.groups : [],
        entries: legs.length
          ? [{
            id: `${suggestionId}-entry`,
            kind: 'parlay',
            label: 'parlay',
            selection_ids: selectionIds,
            ...(totalOdds ? { odds: totalOdds } : {}),
            price_kind: quote.price_kind || undefined,
          }]
          : [],
      },
    },
  }, fallbackId)
}
